/**
 * ContactNoteTimeline
 * -----------------------------------------------------------------------------
 * Lista somente-leitura de eventos do ciclo de vida de uma anotação:
 * criação, edições, leituras, anexos adicionados/removidos.
 *
 * Reaproveita SmartAvatar + ícones lucide já usados no sistema; visual segue o
 * padrão dos cards do `TaskActivityTimeline` (avatar à esquerda + texto + data).
 *
 * @version 1.0.0
 * RELEASE: 2026-06-03 v1.0.0 - Criação inicial (timeline de eventos da nota).
 */
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
  CheckCheck,
  Eye,
  Loader2,
  Paperclip,
  Pencil,
  Plus,
  Trash2,
} from 'lucide-react';

import { SmartAvatar } from '@/components/ui/smart-avatar';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { cn } from '@/lib/utils';

/** Tipos de evento suportados (espelha o CHECK do DB). */
type EventType =
  | 'created'
  | 'edited'
  | 'read'
  | 'attachment_added'
  | 'attachment_removed';

interface NoteEvent {
  id: string;
  event_type: EventType;
  actor_id: string | null;
  actor_name: string | null;
  actor_avatar: string | null;
  metadata: Record<string, unknown> | null;
  created_at: string;
}

interface Props {
  /** Quando null, exibe estado vazio (nota ainda não criada). */
  noteId: string | null;
}

/** Mapa de ícone + label legível para cada tipo de evento. */
function getEventInfo(ev: NoteEvent): {
  icon: React.ReactNode;
  label: string;
  tone: string;
} {
  const file = (ev.metadata?.file_name as string) || 'arquivo';
  switch (ev.event_type) {
    case 'created':
      return {
        icon: <Plus className="h-3.5 w-3.5" />,
        label: 'criou a anotação',
        tone: 'text-emerald-600 dark:text-emerald-400',
      };
    case 'edited':
      return {
        icon: <Pencil className="h-3.5 w-3.5" />,
        label: 'editou a anotação',
        tone: 'text-blue-600 dark:text-blue-400',
      };
    case 'read':
      return {
        icon: <Eye className="h-3.5 w-3.5" />,
        label: 'marcou como lida',
        tone: 'text-violet-600 dark:text-violet-400',
      };
    case 'attachment_added':
      return {
        icon: <Paperclip className="h-3.5 w-3.5" />,
        label: `adicionou o anexo "${file}"`,
        tone: 'text-amber-600 dark:text-amber-400',
      };
    case 'attachment_removed':
      return {
        icon: <Trash2 className="h-3.5 w-3.5" />,
        label: `removeu o anexo "${file}"`,
        tone: 'text-destructive',
      };
    default:
      return {
        icon: <CheckCheck className="h-3.5 w-3.5" />,
        label: ev.event_type,
        tone: 'text-muted-foreground',
      };
  }
}

export function ContactNoteTimeline({ noteId }: Props) {
  const { currentTenant } = useAuth();

  const { data, isLoading } = useQuery({
    queryKey: ['contact-note-events', noteId, currentTenant?.id],
    enabled: !!noteId,
    queryFn: async (): Promise<NoteEvent[]> => {
      const { data, error } = await supabase.rpc('list_contact_note_events', {
        p_note_id: noteId,
        p_tenant_hint: currentTenant?.id ?? null,
      } as never);
      if (error) throw error;
      return (data as unknown as NoteEvent[]) ?? [];
    },
  });

  // Mostra mais recentes no topo dentro do painel.
  const events = useMemo(
    () => [...(data ?? [])].reverse(),
    [data],
  );

  if (!noteId) {
    return (
      <div className="text-xs text-muted-foreground p-4 border border-dashed rounded-md">
        A timeline aparecerá após a primeira ação na anotação.
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-xs text-muted-foreground p-4">
        <Loader2 className="h-3 w-3 animate-spin" /> Carregando timeline...
      </div>
    );
  }

  if (events.length === 0) {
    return (
      <div className="text-xs text-muted-foreground p-4 border border-dashed rounded-md">
        Sem eventos registrados ainda.
      </div>
    );
  }

  return (
    <ol className="relative space-y-3">
      {events.map((ev) => {
        const info = getEventInfo(ev);
        return (
          <li
            key={ev.id}
            className="flex items-start gap-3 rounded-md border bg-card/40 p-2.5"
          >
            <SmartAvatar
              size="xs"
              src={ev.actor_avatar ?? undefined}
              name={ev.actor_name ?? undefined}
            />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5 text-xs">
                <span className={cn('inline-flex items-center', info.tone)}>
                  {info.icon}
                </span>
                <span className="font-medium truncate">
                  {ev.actor_name || 'Usuário'}
                </span>
                <span className="text-muted-foreground truncate">
                  {info.label}
                </span>
              </div>
              <div className="text-[11px] text-muted-foreground mt-0.5">
                {formatDistanceToNow(new Date(ev.created_at), {
                  addSuffix: true,
                  locale: ptBR,
                })}
              </div>
            </div>
          </li>
        );
      })}
    </ol>
  );
}
